import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Figma, Loader2, Check, AlertCircle, Code, LayoutGrid, Eye, EyeOff,
} from 'lucide-react';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'figma_personal_token';

/**
 * Figma Import Modal — fetches a Figma design and converts it to page blocks.
 *
 * Props:
 *   isOpen        - boolean
 *   onClose       - () => void
 *   siteId        - string
 *   onBlocksReady - (blocks: Block[], message: string) => void
 *   getAuthHeader - () => { Authorization: string }
 */
export default function FigmaImportModal({ isOpen, onClose, siteId, onBlocksReady, getAuthHeader }) {
  const [figmaUrl, setFigmaUrl] = useState('');
  const [figmaToken, setFigmaToken] = useState('');
  const [rememberToken, setRememberToken] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [mode, setMode] = useState('blocks'); // 'blocks' | 'html'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Load saved token on mount
  useEffect(() => {
    const saved = localStorage.getItem(TOKEN_KEY);
    if (saved) {
      setFigmaToken(saved);
      setRememberToken(true);
    }
  }, []);

  const isValidUrl = (url) => /figma\.com\/(design|file|proto)\/[a-zA-Z0-9]+/.test(url);

  const handleImport = async () => {
    setError('');

    if (!figmaUrl.trim()) {
      setError('Please paste a Figma URL');
      return;
    }
    if (!isValidUrl(figmaUrl)) {
      setError('Invalid Figma URL. Use a link from figma.com/design/... or figma.com/file/...');
      return;
    }
    if (!figmaToken.trim()) {
      setError('A personal access token is required. Generate one at figma.com/developers/api#access-tokens');
      return;
    }

    // Save or clear token
    if (rememberToken) {
      localStorage.setItem(TOKEN_KEY, figmaToken);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }

    setIsLoading(true);
    try {
      const headers = getAuthHeader();
      const res = await axios.post(`${API}/sites/${siteId}/ai/import-figma`, {
        figma_url: figmaUrl,
        figma_token: figmaToken,
        mode,
      }, { headers });

      const { blocks, message, frame_name } = res.data;
      toast.success(`Imported "${frame_name}" — ${blocks.length} block${blocks.length !== 1 ? 's' : ''}`);
      onBlocksReady(blocks, message);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Import failed';
      setError(detail);
      toast.error(detail);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center">
              <Figma className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Import from Figma
              </h2>
              <p className="text-[11px] text-stone-400">Convert a Figma design to page blocks</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="p-1.5 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-stone-400" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Figma URL */}
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Figma URL</label>
            <input
              type="url"
              value={figmaUrl}
              onChange={(e) => { setFigmaUrl(e.target.value); setError(''); }}
              placeholder="https://www.figma.com/design/abc123/My-Design?node-id=1-2"
              disabled={isLoading}
              className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 placeholder:text-stone-300 disabled:opacity-50"
            />
            <p className="text-[10px] text-stone-400 mt-1">
              Tip: Select a specific frame in Figma and copy the URL for best results
            </p>
          </div>

          {/* Token */}
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Personal Access Token</label>
            <div className="relative">
              <input
                type={showToken ? 'text' : 'password'}
                value={figmaToken}
                onChange={(e) => { setFigmaToken(e.target.value); setError(''); }}
                placeholder="figd_..."
                disabled={isLoading}
                className="w-full px-3 py-2 pr-9 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 placeholder:text-stone-300 disabled:opacity-50 font-mono"
              />
              <button
                type="button"
                onClick={() => setShowToken(!showToken)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
              >
                {showToken ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <input
                type="checkbox"
                id="remember-token"
                checked={rememberToken}
                onChange={(e) => setRememberToken(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-stone-300 text-stone-900 focus:ring-stone-900/20"
              />
              <label htmlFor="remember-token" className="text-[11px] text-stone-500">
                Remember token in this browser
              </label>
            </div>
          </div>

          {/* Mode toggle */}
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Import Mode</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setMode('blocks')}
                disabled={isLoading}
                className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all text-left ${
                  mode === 'blocks'
                    ? 'border-stone-900 bg-stone-50'
                    : 'border-stone-200 hover:border-stone-300'
                }`}
              >
                <LayoutGrid className={`w-4 h-4 ${mode === 'blocks' ? 'text-stone-900' : 'text-stone-400'}`} />
                <div>
                  <div className="text-xs font-semibold text-stone-700">Smart Blocks</div>
                  <div className="text-[10px] text-stone-400">Maps to native block types</div>
                </div>
              </button>
              <button
                onClick={() => setMode('html')}
                disabled={isLoading}
                className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all text-left ${
                  mode === 'html'
                    ? 'border-stone-900 bg-stone-50'
                    : 'border-stone-200 hover:border-stone-300'
                }`}
              >
                <Code className={`w-4 h-4 ${mode === 'html' ? 'text-stone-900' : 'text-stone-400'}`} />
                <div>
                  <div className="text-xs font-semibold text-stone-700">Faithful HTML</div>
                  <div className="text-[10px] text-stone-400">Pixel-perfect CSS recreation</div>
                </div>
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-600">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-stone-100 bg-stone-50/50">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={isLoading}
            className="text-xs"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleImport}
            disabled={isLoading || !figmaUrl.trim() || !figmaToken.trim()}
            className="text-xs bg-stone-900 hover:bg-stone-800 text-white gap-1.5"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Check className="w-3.5 h-3.5" />
                Import Design
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
